import { useAuth0 } from '@auth0/auth0-react'
import { Grid } from '@mui/material'
import { useContext, useEffect, useRef, useState } from 'react'
import AppContext from '../../../context/appContext'
import { useFarm } from '../../../hooks/data/useFarm'
import { useSamplingAreas } from '../../../hooks/data/useSamplingAreas'
import { useSoilsIntersections } from '../../../hooks/data/useSoils'
import ClassificationMap from '../ClassificationMap'
import ClassificationPreview from '../ClassificationPreview'
import StratificationPanel from './StratificationPanel'

// Seeds once per farm and server strata set, not on every refetch or empty local list: deleting the
// last stratum while re-stratifying must not bring the server strata back. Geometry is left out;
// the panel's own sync refreshes the content.
const strataSeedSignature = (farmId, samplingAreas) =>
  `${farmId}|${samplingAreas.map(area => area.properties.id).join(',')}`

const StratificationHome = ({ canEditClassification, setTabValue, mapWidth, mapHeight }) => {
  const { user } = useAuth0()

  const [samplingAreasFeatures, setSamplingAreasFeatures] = useState([])
  const [mapAction, setMapAction] = useState(null)
  const [selectedArea, setSelectedArea] = useState()
  const [selectedAreaType, setSelectedAreaType] = useState()
  const [wasSaved, setWasSaved] = useState(false)
  const [georaster, setGeoraster] = useState()
  const [previewSampling, setPreviewSampling] = useState(null)
  const [vectorized, setVectorized] = useState(false)
  const [drawActivated, setDrawActivated] = useState(false)
  const [restratification, setRestratification] = useState(false)
  const { currentFarm, programConfig } = useContext(AppContext)
  const seededSignatureRef = useRef(null)

  const { data: farmData } = useFarm(currentFarm?.id, user)
  const { data: samplingAreas } = useSamplingAreas({ farmId: currentFarm?.id })

  const stratasConfig = programConfig?.stratasConfigEndpoint || {}
  const supportedCreationModes = programConfig?.stratificationModesAllowed ?? null

  const isEditing = drawActivated && !wasSaved

  useEffect(() => {
    if (!farmData || !samplingAreas?.length || isEditing) return

    const signature = strataSeedSignature(currentFarm?.id, samplingAreas)
    if (seededSignatureRef.current === signature) return

    seededSignatureRef.current = signature
    setSamplingAreasFeatures(samplingAreas)
    setWasSaved(true)
  }, [farmData, samplingAreas, isEditing, currentFarm?.id])

  const handleSetGeoraster = val => {
    setGeoraster(val)
    if (val) {
      setPreviewSampling(null)
    }
  }

  const handleSetPreviewSampling = val => {
    setPreviewSampling(val)
    if (val) {
      setGeoraster(null)
    }
  }

  const perimeter = farmData?.toGeoJSON
  const { data: soils } = useSoilsIntersections(currentFarm?.id, perimeter)

  const layers = [
    {
      name: 'Suelos',
      features: soils,
    },
  ]

  return (
    <>
      {perimeter && supportedCreationModes && (
        <Grid container>
          <Grid item xs={4}>
            <StratificationPanel
              canEditClassification={canEditClassification}
              drawActivated={drawActivated}
              farm={currentFarm}
              georaster={georaster}
              mapAction={mapAction}
              perimeter={perimeter}
              previewSampling={previewSampling}
              restratification={restratification}
              samplingAreas={samplingAreas}
              samplingAreasFeatures={samplingAreasFeatures}
              selectedArea={selectedArea}
              setDrawActivated={setDrawActivated}
              setGeoraster={handleSetGeoraster}
              setMapAction={setMapAction}
              setPreviewSampling={handleSetPreviewSampling}
              setRestratification={setRestratification}
              setSamplingAreasFeatures={setSamplingAreasFeatures}
              setSelectedArea={setSelectedArea}
              setSelectedAreaType={setSelectedAreaType}
              setTabValue={setTabValue}
              setVectorized={setVectorized}
              setWasSaved={setWasSaved}
              stratasConfig={stratasConfig}
              supportedCreationModes={supportedCreationModes}
              wasSaved={wasSaved}
            />
          </Grid>
          <Grid item xs={8}>
            {(drawActivated || (!georaster && !vectorized)) && (
              <ClassificationMap
                allowSelfIntersection={drawActivated && vectorized}
                drawActivated={drawActivated}
                layers={layers}
                mapAction={mapAction}
                mapHeight={mapHeight}
                mapWidth={mapWidth}
                perimeter={perimeter}
                samplingAreasFeatures={samplingAreasFeatures}
                selectedArea={selectedArea}
                selectedAreaType={selectedAreaType}
                setDrawActivated={setDrawActivated}
                setMapAction={setMapAction}
                setSamplingAreasFeatures={setSamplingAreasFeatures}
                setSelectedArea={setSelectedArea}
                setSelectedAreaType={setSelectedAreaType}
                vectorized={vectorized}
                wasSaved={wasSaved}
              />
            )}
            {(georaster || previewSampling) && !drawActivated && (
              <ClassificationPreview
                georaster={georaster}
                mapHeight={mapHeight}
                mapWidth={mapWidth}
                perimeter={perimeter}
                samplingAreas={previewSampling}
              />
            )}
          </Grid>
        </Grid>
      )}
    </>
  )
}

export default StratificationHome
